'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AgentId, AgentRestriction, AgentRestrictions, AgentType } from '@/lib/agent-types';
import { logger } from '@/lib/logger';
import {
  DEEP_DATA_AGENT,
  getDeepDataRestriction,
  getDeepDataUsageStorageKey,
  getFeatureRestrictionForErrorCode,
  isDeepDataAgent,
  type FeatureRestriction,
} from '@/lib/feature-permissions';

interface UseDeepDataAccessOptions {
  authLoading: boolean;
  userId?: unknown;
  userLevel: unknown;
}

interface FeatureDialogState {
  open: boolean;
  restriction: FeatureRestriction;
}

export function useDeepDataAccess({ authLoading, userId, userLevel }: UseDeepDataAccessOptions) {
  const [featureDialog, setFeatureDialog] = useState<FeatureDialogState>({ open: false, restriction: 'upgrade' });
  const [deepDataUsedToday, setDeepDataUsedToday] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const usageKey = getDeepDataUsageStorageKey(userId);
    setDeepDataUsedToday(localStorage.getItem(usageKey) === '1');
  }, [userId]);

  const markDeepDataUsedToday = useCallback(() => {
    setDeepDataUsedToday(true);
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(getDeepDataUsageStorageKey(userId), '1');
    } catch (error) {
      logger.warn('[DeepDataAccess] Failed to persist usage marker:', error);
    }
  }, [userId]);

  const deepDataRestriction = authLoading ? null : getDeepDataRestriction(userLevel, deepDataUsedToday);

  const agentRestrictions = useMemo<AgentRestrictions>(() => {
    if (!deepDataRestriction) return {};

    return {
      [DEEP_DATA_AGENT]: deepDataRestriction === 'daily-limit' ? 'daily-limit' : 'locked',
    };
  }, [deepDataRestriction]);

  const showFeatureRestriction = useCallback((restriction: FeatureRestriction) => {
    setFeatureDialog({ open: true, restriction });
  }, []);

  const setFeatureDialogOpen = useCallback((open: boolean) => {
    setFeatureDialog((prev) => ({ ...prev, open }));
  }, []);

  const handleRestrictedAgentClick = useCallback(
    (_agent: AgentId, restriction: AgentRestriction) => {
      if (restriction === 'daily-limit') {
        showFeatureRestriction('daily-limit');
        return;
      }

      showFeatureRestriction(deepDataRestriction || 'upgrade');
    },
    [deepDataRestriction, showFeatureRestriction],
  );

  const guardAgentAccess = useCallback(
    (agent: AgentType): boolean => {
      if (!isDeepDataAgent(agent)) return true;
      if (authLoading) return false;

      const restriction = getDeepDataRestriction(userLevel, deepDataUsedToday);
      if (!restriction) return true;

      showFeatureRestriction(restriction);
      return false;
    },
    [authLoading, deepDataUsedToday, showFeatureRestriction, userLevel],
  );

  const markAgentUsed = useCallback(
    (agent: AgentType) => {
      if (isDeepDataAgent(agent)) {
        markDeepDataUsedToday();
      }
    },
    [markDeepDataUsedToday],
  );

  const isAgentRestricted = useCallback(
    (agent: AgentType): boolean => isDeepDataAgent(agent) && !!deepDataRestriction,
    [deepDataRestriction],
  );

  const showFeatureRestrictionForErrorCode = useCallback(
    (code: unknown): FeatureRestriction | null => {
      const restriction = getFeatureRestrictionForErrorCode(code);
      if (restriction) {
        showFeatureRestriction(restriction);
      }
      return restriction;
    },
    [showFeatureRestriction],
  );

  return {
    agentRestrictions,
    featureDialog,
    guardAgentAccess,
    handleRestrictedAgentClick,
    isAgentRestricted,
    markAgentUsed,
    setFeatureDialogOpen,
    showFeatureRestriction,
    showFeatureRestrictionForErrorCode,
  };
}
